import { SearchspringCollectionProvider } from '../fetchers/collection'
import { useScrollCache } from '../hooks/useScrollCache'
import { useInfiniteScrollResults } from '../hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from './InfiniteScroll'
import { useScrollPositionKeeper } from '../hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from '@/components/helpers/scroll-history'
import { useFilterScroll } from '../hooks/useFilterScroll'
import { ResultsCard } from './ResultCard'
import { FilterSort } from './FilterSort'
import { FilterBarMobile } from './FilterBarMobile'
import { ResultCount } from './ResultCount'
import { FilterSidebar } from './FilterSidebar'
import { useEffect, useState, useMemo } from 'preact/hooks'
import useOfflineWishlist from '@/components/hooks/useOfflineWishlist'
import { SkeletonCard } from './SkeletonCard'
import { useWishlist } from '@/components/hooks/useWishlist'

function CollectionGrid() {
  history.scrollRestoration = 'manual'
  const { allResults, gridChange, isLoading } = useInfiniteScrollResults()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const [firstLoad, setFirstLoad] = useState(true)
  const { setScrollElement } = useFilterScroll()
  const { wishlist: offlineWishlist, getKeys, ...callbacks } = useOfflineWishlist()
  const { wishlists: omneoWishlists } = useWishlist()

  const wishlistItems = useMemo(() => {
    return getKeys()
  }, [offlineWishlist])

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useScrollCache([allResults, firstLoad])

  return (
    <div className="flex flex-col">
      <div className="bg-[--bg-color]">
        <div className={'mx-auto flex w-full items-center justify-between px-4 py-6 md:px-10'}>
          {collection?.seo?.links?.length === 0 ? null : (
            <div className="flex items-center flex-grow w-full gap-4 overflow-x-auto md:gap-6">
              {collection?.seo?.links?.map((link) => (
                <a className="underline btn btn-text-black btn-inline btn-md" href={link.url}>
                  {link.text}
                </a>
              ))}
            </div>
          )}
          <div className="items-center hidden gap-6 md:flex">
            <ResultCount className="pl-2 whitespace-nowrap text-neutrals-secondary-grey" />
            <FilterSort />
          </div>
        </div>
      </div>
      <FilterBarMobile />
      <div
        ref={setScrollElement}
        className="grid w-full grid-cols-1 py-6 pt-0 mx-auto md:grid-cols-4 md:gap-10 md:px-10 md:py-10"
      >
        <div className="sticky hidden col-span-1 top-48 md:block">
          <FilterSidebar />
        </div>

        <div
          className="grid grid-cols-2 col-span-1 h-fit gap-x-2 gap-y-6 md:col-span-3 md:grid-cols-3 md:gap-x-4"
          ref={setEntry}
        >
          <InfiniteScrollPrevious className={'col-span-2 md:col-span-3'} />
          {allResults.length > 0 && !firstLoad ? (
            <>
              {allResults?.map((item, index) => {
                return 'value' in item ? (
                  <div
                    // TODO: when ready for multi device assets, swap classes here
                    // className="relative col-span-full my-4 aspect-[16/9] md:col-span-1 md:my-0 md:aspect-9/16"
                    className="relative col-span-full aspect-square md:col-span-1"
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: item.value }}
                      // TODO: when ready for multi device assets, swap classes here
                      // className="absolute overflow-hidden -inset-4 md:static"
                      className="absolute inset-0"
                      key={item.value}
                    />
                  </div>
                ) : (
                  <ResultsCard
                    wishlist={{
                      omneoWishlists,
                      offlineWishlistItems: wishlistItems,
                      wsCallbacks: callbacks,
                      ...(item.handle.includes('gift-card') && { type: 'none' })
                    }}
                    pageType="collection"
                    result={item}
                    index={index}
                    key={item.handle}
                    className="wk-wishlist-button-target"
                  />
                )
              })}
            </>
          ) : (
            Array.from({ length: 12 }).map((_, i) => <SkeletonCard className={'transition-all duration-300'} key={i} />)
          )}
        </div>
      </div>
      {allResults.length > 0 && !firstLoad && (
        <div className="flex justify-center px-4 py-6">
          <InfiniteScrollNext />
        </div>
      )}
    </div>
  )
}

export function WrappedCollectionGrid() {
  scrollHistoryManager.updateHistoryState()

  return (
    <SearchspringCollectionProvider handle={collection?.handle} resultsPerPage={settings?.per_page}>
      <CollectionGrid />
    </SearchspringCollectionProvider>
  )
}
